import Box from '@mui/material/Box';
import { Instance } from '@popperjs/core';
import { addSeconds, differenceInMilliseconds } from 'date-fns';
import { useCallback, useMemo, useRef, useState } from 'react';

import displayWallClock from '@/shared/services/displayWallClock';
import { BootstrapTooltip } from './BootstrapTooltip';

import { useTimestampState } from '@/shared/hooks/websocket/useTimestamp';
import { GamePlayBackPositions } from './PlayerControls';
import { useCurrentGame } from '@/features/games/context/CurrentGameContext';
import { useGameTimeFormatter } from '@/features/games/hooks/useGameTimeFormatter';
import { useSportEvents } from '@/features/ses';
import { formatGameTime } from '@/features/ses/utils/sportEventUtils';
import { decodePeriodDisplay } from '@/shared/utils/getPeriodName';
import SegmentedSlider, { SliderSegment } from './SegmentedSlider';
import Tracking from '@/shared/services/tracking';
import { PlaybackRange } from '@/shared/types/GameStateModel';
import { buildTimelinePeriodRanges, snapToPlayablePosition } from '../utils/timelinePeriods';

type Props = {
  gamePlaybackPositions: GamePlayBackPositions;
  gameId: string;
};

const MARK_HEIGHT = 5;

const isWithinRange = (timestamp: number, range: PlaybackRange) =>
  timestamp >= range.start / 1_000_000 && timestamp < range.end / 1_000_000;

const PlaybackPositionSlider = ({ gamePlaybackPositions }: Props) => {
  const gameContext = useCurrentGame();
  const { getActiveTimezone } = useGameTimeFormatter();

  const [, setTimestamp] = useTimestampState();

  const sliderRef = useRef<HTMLInputElement>(null);
  const popperRef = useRef<Instance>(null);

  const periodNamingMapping = gameContext?.gameTypeMetadata?.periodNamingMapping?.[0];
  const seasonType = gameContext?.game?.season?.seasonType;

  const { timeMappingRecords, findTimeMappingForTimestamp } = useSportEvents();

  const playbackPositionSeconds = Math.round(
    differenceInMilliseconds(gamePlaybackPositions.head, gamePlaybackPositions.start) / 1000,
  );
  const effectiveLive = Math.max(
    new Date(gamePlaybackPositions.live).getTime(),
    new Date(gamePlaybackPositions.head).getTime(),
  );
  const endPositionSeconds = Math.max(
    0,
    differenceInMilliseconds(effectiveLive, gamePlaybackPositions.start) / 1000,
  );

  const getStartTimestamp = useCallback(() => gamePlaybackPositions.start, [gamePlaybackPositions.start]);

  // The player owns video availability. Game-time mappings only annotate the
  // actual wall-clock timeline; they must never change its duration.
  const segments: SliderSegment[] = useMemo(() => {
    const gameStart = getStartTimestamp();
    const gameEnd = gameStart + endPositionSeconds * 1000;

    if (!Number.isFinite(gameStart) || endPositionSeconds <= 0) return [];

    // Each phase (period, interval, overtime) is an annotation range. The
    // unannotated portions remain in the timeline instead of being collapsed.
    const periodRanges = buildTimelinePeriodRanges(timeMappingRecords, periodNamingMapping, seasonType);

    const visiblePeriodRanges = periodRanges
      .map((range) => ({
        ...range,
        start: Math.max(range.start, gameStart),
        end: Math.min(range.end, gameEnd),
      }))
      .filter((range) => range.end > range.start);

    const unavailableRanges = (gamePlaybackPositions.unavailableRanges ?? [])
      .map((range) => ({
        start: Math.max(range.start / 1_000_000, gameStart),
        end: Math.min(range.end / 1_000_000, gameEnd),
      }))
      .filter((range) => range.end > range.start);

    // Split the complete wall-clock window wherever availability or a known
    // period changes. Every second remains represented, including gaps and
    // intervals without SES data.
    const boundaries = new Set<number>([gameStart, gameEnd]);
    for (const range of [...visiblePeriodRanges, ...unavailableRanges]) {
      boundaries.add(range.start);
      boundaries.add(range.end);
    }
    const points = Array.from(boundaries).sort((a, b) => a - b);
    const result: SliderSegment[] = [];

    for (let index = 0; index < points.length - 1; index += 1) {
      const start = points[index];
      const end = points[index + 1];
      const midpoint = start + (end - start) / 2;
      const unavailable = unavailableRanges.some((range) => midpoint >= range.start && midpoint < range.end);
      const periodRange = visiblePeriodRanges.find(
        (range) => midpoint >= range.start && midpoint < range.end,
      );
      const code = periodRange?.code ?? 0;
      const label = code === 0 ? '' : decodePeriodDisplay(code, periodNamingMapping, seasonType).full;
      const startSec = (start - gameStart) / 1000;
      const endSec = (end - gameStart) / 1000;
      const previous = result[result.length - 1];

      if (
        previous &&
        previous.period === code &&
        previous.label === label &&
        previous.available === !unavailable &&
        previous.endSec === startSec
      ) {
        previous.endSec = endSec;
      } else {
        // An unannotated mapping outage must remain a separate data segment
        // (tooltip falls back to wall clock), but it is still playable video.
        // Do not draw a visual divider around it; reserve dividers for a real
        // phase change or an unavailable-video boundary.
        const dividerBefore =
          previous !== undefined &&
          (previous.available !== !unavailable ||
            (previous.label !== '' && label !== '' && previous.period !== code));
        result.push({
          id: `${code}-${start}`,
          period: code,
          label,
          startSec,
          endSec,
          available: !unavailable,
          dividerBefore,
        });
      }
    }

    return result;
  }, [
    timeMappingRecords,
    endPositionSeconds,
    getStartTimestamp,
    gamePlaybackPositions.unavailableRanges,
    periodNamingMapping,
    seasonType,
  ]);

  // A mapping is a label only while its phase is actually annotated. This
  // prevents a frozen final Q4 value from labelling the postgame footage.
  const findGameTimeForTimestamp = useCallback(
    (targetUtc: number): string | null => {
      const targetSeconds = (targetUtc - gamePlaybackPositions.start) / 1000;
      const isAnnotated = segments.some(
        (segment) =>
          segment.period !== 0 && targetSeconds >= segment.startSec && targetSeconds < segment.endSec,
      );
      if (!isAnnotated) return null;

      const mapping = findTimeMappingForTimestamp(targetUtc);
      if (!mapping || mapping.period === undefined || mapping.gameTime === undefined) return null;

      return `${decodePeriodDisplay(mapping.period, periodNamingMapping, seasonType).abbreviated} ${formatGameTime(mapping.gameTime)}`;
    },
    [
      findTimeMappingForTimestamp,
      gamePlaybackPositions.start,
      periodNamingMapping,
      seasonType,
      segments,
    ],
  );

  // Update the timeLabel function to use the loaded state:
  const timeLabel = useCallback(
    (seconds: number, useGameTime = false) => {
      if (Number.isNaN(seconds)) return '00:00:00';

      if (useGameTime) {
        const startTimestamp = getStartTimestamp();
        const targetUtc = startTimestamp + seconds * 1000;
        if ((gamePlaybackPositions.unavailableRanges ?? []).some((range) => isWithinRange(targetUtc, range))) {
          return `Video unavailable · ${displayWallClock(new Date(targetUtc), { timezone: getActiveTimezone() })}`;
        }
        if (timeMappingRecords.length > 0) {
          const gameTime = findGameTimeForTimestamp(targetUtc);

          if (gameTime) {
            return gameTime;
          }
        }
      }

      return displayWallClock(addSeconds(gamePlaybackPositions.start, seconds), {
        timezone: getActiveTimezone(),
      });
    },
    [
      timeMappingRecords.length,
      getStartTimestamp,
      findGameTimeForTimestamp,
      gamePlaybackPositions.start,
      gamePlaybackPositions.unavailableRanges,
      getActiveTimezone,
    ],
  );

  const nearestPlayablePosition = useCallback(
    (seconds: number) =>
      snapToPlayablePosition(
        seconds,
        getStartTimestamp(),
        endPositionSeconds,
        gamePlaybackPositions.unavailableRanges ?? [],
      ),
    [endPositionSeconds, gamePlaybackPositions.unavailableRanges, getStartTimestamp],
  );

  const handleScrub = useCallback(
    (newPlaybackPosSeconds: number) => {
      if (endPositionSeconds <= 0) return;

      const playablePosition = nearestPlayablePosition(newPlaybackPosSeconds);
      if (playablePosition !== null && playablePosition >= 0 && playablePosition <= endPositionSeconds) {
        if (setTimestamp){
          setTimestamp(addSeconds(gamePlaybackPositions.start, playablePosition));
          Tracking.getInstance().track('Seek', {
            category: 'Controls',
            source: 'Playback Position Slider',
          });
        }
      }
    },
    [endPositionSeconds, nearestPlayablePosition, setTimestamp, gamePlaybackPositions.start],
  );

  const [tooltipTimePosition, setTooltipTimePosition] = useState<string>('');

  const positionRef = useRef<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });

  return (
    <Box
      mt={`${MARK_HEIGHT}px`}
      mx={1}
      sx={{ alignSelf: 'stretch', position: 'relative', display: 'flex' }}
    >
      <BootstrapTooltip
        title={tooltipTimePosition}
        key="timestamp"
        placement="top"
        slotProps={{
          popper: {
            popperRef,
            anchorEl: {
              getBoundingClientRect: () =>
                new DOMRect(
                  positionRef.current.x,
                  sliderRef.current?.getBoundingClientRect().y ??
                    positionRef.current.y,
                  0,
                  0,
                ),
            },
          },
        }}
      >
        <Box ref={sliderRef} sx={{ width: '100%' }}>
          <SegmentedSlider
            segments={segments}
            value={playbackPositionSeconds}
            max={endPositionSeconds}
            onChange={handleScrub}
            onTimeLabel={(seconds) => timeLabel(seconds, true)}
            popperRef={popperRef}
            positionRef={positionRef}
            setTooltipTime={setTooltipTimePosition}
          />
        </Box>
      </BootstrapTooltip>
    </Box>
  );
};

export default PlaybackPositionSlider;
